import { ButtonGroup } from '@design/components/button-group/ButtonGroup';

import { CloseTrigger, Drawer } from '@design/components/drawer/Drawer';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useScrollListener } from '@helpers/hooks/use-scroll-listener/useScrollListener';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { NoDataFound } from '@terminal/components/no-data-found/NoDataFound';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useQueryInsights } from '@terminal/react-query-hooks/insights/useInsight';
import { useKgInsightsByIdResult } from '@terminal/react-query-hooks/insights/useKgInsight';
import {
  CATEGORY_ORDER,
  usePulseSentimentInsights,
} from '@terminal/react-query-hooks/themes/usePulseSentimentInsights';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { useCatalystDrawer } from '@terminal/widgets/catalyst-insights-drawer/useCatalystDrawer';
import { useEarningsInsightDrawer } from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { useMarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/useMarketCatalystDrawer';
import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePulseMonthlySummaries } from '../hooks/usePulseSummaries';
import { useSentimentScope } from '../hooks/useSentimentScope';
import {
  PulseInsightCategory,
  PulseMonthlySummaries,
  PulseSentimentDirection,
  PulseSentimentInsight,
  PulseSubject,
} from '../pulse-types';
import { pinnedInsightIdsByTypeForDirection } from '../utils/citationIdsForDirection';
import { openDrawerForInsight } from '../utils/openDrawerForInsight';
import {
  toPulseInsight,
  toPulseScenarioInsight,
} from '../utils/toPulseInsight';
import { CATEGORY_CLASS } from './category-pill/CategoryPill';
import pillStyles from './category-pill/CategoryPill.module.scss';
import styles from './MonthlySentimentDrawer.module.scss';
import { PulseInsightCard } from './PulseInsightCard';

const LOAD_MORE_OFFSET = 100;
const DRAWER_SKELETON_AREAS = [
  { styles: { width: '100%', height: '32px', borderRadius: '8px' } },
  { styles: { width: '100%', height: '160px', borderRadius: '8px' } },
  { styles: { width: '100%', height: '160px', borderRadius: '8px' } },
];
type PinnedInsightIds = ReturnType<typeof pinnedInsightIdsByTypeForDirection>;

const INSIGHT_DRAWER_VALUES = new Set<string>([
  queryValues.insightsDrawer,
  queryValues.earningsInsightDrawer,
  queryValues.catalystInsightsDrawer,
  queryValues.marketCatalystDrawer,
]);

const isPulseDirection = (
  value: string | null
): value is PulseSentimentDirection =>
  value === 'tailwind' || value === 'headwind' || value === 'all';

const keyDriverLabel = (
  direction: PulseSentimentDirection,
  isAssetOverview: boolean
) => {
  if (direction === 'all') {
    return undefined;
  }

  if (!isAssetOverview) {
    return direction;
  }

  return direction === 'tailwind' ? 'bullish' : 'bearish';
};

const DrawerContent = ({
  subject,
  pinnedInsightIds,
  direction,
  showAllDrivers,
  isAssetOverview,
  source,
  onOpenInsight,
}: {
  subject: PulseSubject;
  pinnedInsightIds: PinnedInsightIds;
  direction: PulseSentimentDirection;
  showAllDrivers: boolean;
  isAssetOverview: boolean;
  source: PosthogTrackingSource;
  onOpenInsight: (insight: PulseSentimentInsight) => void;
}) => {
  const { t } = useTranslation(['widget']);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] =
    useState<PulseInsightCategory | null>(null);
  const theme = subject.type === 'theme' ? subject.item : undefined;
  const sentimentScope = useSentimentScope(theme);

  const { kgInsightIds, scenarioInsightIds } = pinnedInsightIds;
  const { data: pinnedKgInsightsById, isLoading: arePinnedKgInsightsLoading } =
    useKgInsightsByIdResult(kgInsightIds);
  const {
    data: pinnedScenarioInsights,
    isLoading: arePinnedScenarioInsightsLoading,
  } = useQueryInsights(scenarioInsightIds);
  const pinnedInsights: PulseSentimentInsight[] = [
    ...[...pinnedKgInsightsById.values()].map(insight =>
      toPulseInsight(insight, direction)
    ),
    ...pinnedScenarioInsights.map(insight => toPulseScenarioInsight(insight)),
  ];

  const {
    data: allDriverInsights,
    pinnedInsights: enrichedPinnedInsights,
    counts: allDriverCounts,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePulseSentimentInsights({
    subject,
    themeIds: theme ? sentimentScope.themeIds : undefined,
    companyIds: theme ? sentimentScope.companyIds : undefined,
    direction,
    category: activeCategory,
    enabled: !theme || sentimentScope.isReady,
    pinnedInsights,
  });

  const canPageAllDrivers = showAllDrivers && !isError;

  useScrollListener(
    cardsRef,
    () => {
      if (canPageAllDrivers && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }

      return canPageAllDrivers && hasNextPage;
    },
    [
      canPageAllDrivers,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      isLoading,
    ],
    { isGlobalScrollListened: false, offset: LOAD_MORE_OFFSET }
  );

  const arePinnedInsightsLoading =
    arePinnedKgInsightsLoading || arePinnedScenarioInsightsLoading;

  // Basket feeds are company-filtered client-side, so a full API page can
  // add no All Drivers cards. The scroll position then never changes and
  // the scroll listener never fires again, leaving the loader stuck.
  // Key Drivers stays on the pinned list, and a failed page keeps
  // hasNextPage, so both must stay out of this loop.
  useLayoutEffect(() => {
    const cards = cardsRef.current;

    if (
      canPageAllDrivers &&
      sentimentScope.kind === 'basket' &&
      cards &&
      hasNextPage &&
      !isFetchingNextPage &&
      cards.scrollHeight - cards.offsetHeight - cards.scrollTop <=
        LOAD_MORE_OFFSET
    ) {
      void fetchNextPage();
    }
  }, [
    canPageAllDrivers,
    sentimentScope.kind,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const isInitialLoad =
    (!showAllDrivers && arePinnedInsightsLoading) ||
    (showAllDrivers &&
      (sentimentScope.isLoading || (isLoading && !activeCategory)));

  if (isInitialLoad) {
    return (
      <SkeletonLoader
        className={styles.skeletonLoader}
        areas={DRAWER_SKELETON_AREAS}
      />
    );
  }

  if (showAllDrivers && (sentimentScope.isError || isError)) {
    return <ErrorMessageWrapper displayIcon variant="error" />;
  }

  const data = showAllDrivers
    ? allDriverInsights
    : enrichedPinnedInsights.filter(
        insight => !activeCategory || insight.category === activeCategory
      );
  const allDriversEmpty = CATEGORY_ORDER.every(
    category => allDriverCounts[category].count === 0
  );
  const counts = showAllDrivers
    ? allDriverCounts
    : CATEGORY_ORDER.reduce(
        (result, category) => {
          result[category] = {
            count: enrichedPinnedInsights.filter(
              insight => insight.category === category
            ).length,
            hasMore: false,
          };

          return result;
        },
        {} as typeof allDriverCounts
      );
  const isListLoading =
    isLoading && (showAllDrivers || (data.length === 0 && allDriversEmpty));
  const visibleCategories = CATEGORY_ORDER.filter(
    category => category === activeCategory || counts[category].count > 0
  );
  const emptyTitle = showAllDrivers
    ? t('widget:themeOverview.pulse.noDriversInLast30Days')
    : direction === 'all'
      ? t('widget:themeOverview.pulse.noKeyDriversInLast30Days')
      : /* i18next-extract-mark-context-next-line ["tailwind", "headwind", "bullish", "bearish"] */
        t('widget:themeOverview.pulse.drawer.noKeyDriversIdentified', {
          context: keyDriverLabel(direction, isAssetOverview),
        });

  return (
    <div className={styles.content}>
      {visibleCategories.length > 0 && (
        <div className={styles.chips}>
          {visibleCategories.map(category => {
            const { count, hasMore } = counts[category];
            const countLabel = hasMore ? `${count}+` : count;

            return (
              <button
                key={category}
                type="button"
                className={clsx(
                  pillStyles.categoryPill,
                  CATEGORY_CLASS[category],
                  styles.filterChip,
                  { [styles.filterChipActive]: activeCategory === category }
                )}
                aria-pressed={activeCategory === category}
                onClick={() =>
                  setActiveCategory(current =>
                    current === category ? null : category
                  )
                }
              >
                {t(
                  `widget:themeOverview.pulse.drawer.categoryFilter_${category}`,
                  { count: countLabel }
                )}
              </button>
            );
          })}
        </div>
      )}
      <div ref={cardsRef} className={styles.cards}>
        {isListLoading ? (
          <SkeletonLoader
            className={styles.skeletonLoader}
            areas={[
              {
                styles: { width: '100%', height: '160px', borderRadius: '8px' },
              },
              {
                styles: { width: '100%', height: '160px', borderRadius: '8px' },
              },
            ]}
          />
        ) : (
          <>
            {data.map(insight => (
              <PulseInsightCard
                key={insight.id}
                insight={insight}
                source={source}
                onOpen={() => onOpenInsight(insight)}
              />
            ))}
            {data.length === 0 && (
              <NoDataFound
                bannerPath="/app/static/images/empty-state/radio-telescope.png"
                className={styles.empty}
                title={emptyTitle}
                description={
                  direction === 'all'
                    ? t(
                        'widget:themeOverview.pulse.noKeyDriversInLast30DaysDescription'
                      )
                    : undefined
                }
              />
            )}
          </>
        )}
        {showAllDrivers && hasNextPage && (
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: '100px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

const OpenDrawerContent = ({
  subject,
  summaries,
  direction,
  isAssetOverview,
  source,
  onOpenInsight,
}: {
  subject: PulseSubject;
  summaries?: PulseMonthlySummaries;
  direction: PulseSentimentDirection;
  isAssetOverview: boolean;
  source: PosthogTrackingSource;
  onOpenInsight: (insight: PulseSentimentInsight) => void;
}) => {
  const { t } = useTranslation(['widget']);
  const pinnedInsightIds = pinnedInsightIdsByTypeForDirection(
    summaries,
    direction
  );
  const hasKeyDrivers =
    pinnedInsightIds.kgInsightIds.length > 0 ||
    pinnedInsightIds.scenarioInsightIds.length > 0;
  const [showAllDrivers, setShowAllDrivers] = useState(!hasKeyDrivers);

  return (
    <DrawerContentWrapper className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          {isAssetOverview
            ? t(
                `widget:assetOverview.sentiment.monthlyDirectionSentiment_${direction}`
              )
            : t(`widget:themeOverview.pulse.drawer.title_${direction}`)}
        </h2>
        <ButtonGroup
          className={styles.driverScope}
          labels={[
            t('widget:themeOverview.pulse.drawer.keyDrivers'),
            t('widget:themeOverview.pulse.drawer.allDrivers'),
          ]}
          activeIndex={showAllDrivers ? 1 : 0}
          onClick={index => setShowAllDrivers(index === 1)}
          aria-label={t('widget:themeOverview.pulse.drawer.driverScope')}
        />
      </div>
      <DrawerContent
        subject={subject}
        pinnedInsightIds={pinnedInsightIds}
        direction={direction}
        showAllDrivers={showAllDrivers}
        isAssetOverview={isAssetOverview}
        source={source}
        onOpenInsight={onOpenInsight}
      />
    </DrawerContentWrapper>
  );
};

export const MonthlySentimentDrawer = ({
  subject,
  source = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
}: {
  subject: PulseSubject;
  source?: PosthogTrackingSource;
}) => {
  const themeId = subject.type === 'theme' ? subject.item.id : undefined;
  const { params, deleteQueryParam } = useQueryParams();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const { openDrawer: openCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openEarningsInsightDrawer } = useEarningsInsightDrawer();
  const { openDrawer: openScenarioInsightDrawer } = useInsightDrawer();
  const isAssetOverview = source === POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW;
  const direction = params[queryKeys.monthlySentiment];
  const openDirection = isPulseDirection(direction) ? direction : null;
  const isInsightDrawerOpen = INSIGHT_DRAWER_VALUES.has(
    params[queryKeys.activeDrawer] ?? ''
  );
  const subjectId =
    subject.type === 'theme' ? subject.item.id : subject.item.tag;
  const monthlySummaries = usePulseMonthlySummaries({
    id: subjectId,
    subjectType: subject.type,
  });

  const closeDrawer = () =>
    deleteQueryParam(queryKeys.monthlySentiment, { replace: true });

  const openInsight = (insight: PulseSentimentInsight) => {
    openDrawerForInsight({
      insight,
      source,
      fallbackThemeId: themeId,
      openMarketCatalystDrawer,
      openCatalystDrawer,
      openEarningsInsightDrawer,
      openScenarioInsightDrawer,
    });
  };

  return (
    <Drawer
      isOpen={openDirection !== null}
      handleCloseClick={closeDrawer}
      closeTriggers={
        isInsightDrawerOpen
          ? [CloseTrigger.CloseButtonClick, CloseTrigger.BackgroundClick]
          : undefined
      }
      data-testid="pulse-monthly-sentiment-drawer"
    >
      {openDirection &&
        (monthlySummaries.isLoading ? (
          <DrawerContentWrapper className={styles.wrapper}>
            <SkeletonLoader
              className={styles.skeletonLoader}
              areas={DRAWER_SKELETON_AREAS}
            />
          </DrawerContentWrapper>
        ) : (
          <OpenDrawerContent
            key={`${subjectId}-${openDirection}`}
            subject={subject}
            summaries={monthlySummaries.data}
            direction={openDirection}
            isAssetOverview={isAssetOverview}
            onOpenInsight={openInsight}
            source={source}
          />
        ))}
    </Drawer>
  );
};
